import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router";

import mashLogo from "@/assets/logo.png";
import { SignInSheet } from "@/components/auth/AuthBottomSheet";
import { Button } from "@/components/ui/button";
import { markIntroSeen } from "@/lib/firstRunFlags";
import { consumeRedirectAfterAuth } from "@/lib/redirectAfterAuth";
import { useAuthStore } from "@/store/authStore";

export function LoginHomeMobile() {
  const navigate = useNavigate();
  const { user, clearError } = useAuthStore();
  // Every way in lives in the sign-in sheet — Apple, Google and email — so this
  // screen is just the welcome and one button. `/login/email` still exists for
  // deep links and the password-reset return trip. Signing up goes to
  // `/signup`, where the invite gate has to be cleared before its options sheet
  // appears.
  const [signInOpen, setSignInOpen] = useState(false);

  // Reaching the login home means the intro is behind them — whether they
  // watched it, skipped it, or arrived straight here from a deep link. Setting
  // the flag here stops the slides ambushing them on a later launch.
  useEffect(() => {
    markIntroSeen();
  }, []);

  if (user) {
    const redirect = consumeRedirectAfterAuth();
    return <Navigate to={redirect ?? "/"} replace />;
  }

  return (
    <div className="bg-background flex min-h-screen w-full flex-col items-center px-6">
      <div className="flex w-full max-w-sm flex-col items-center pt-32">
        <p className="text-foreground text-center text-2xl font-bold">
          Welcome to
        </p>
        <img
          src={mashLogo}
          alt="Mash"
          className="mt-3 w-full max-w-xs object-contain"
        />
      </div>

      <p className="text-muted-foreground mt-36 text-center text-sm leading-6">
        Sign up or log in to continue
      </p>

      <Button
        onClick={() => {
          clearError();
          setSignInOpen(true);
        }}
        variant="vibrant-mobile"
        className="mt-11 h-12 w-full max-w-64 justify-center gap-2 text-sm font-semibold"
      >
        Log in
        <ArrowRight className="h-4 w-4" />
      </Button>

      <div className="text-muted-foreground mt-8 text-sm">
        Have an invite code?{" "}
        <button
          type="button"
          onClick={() => {
            clearError();
            navigate("/signup");
          }}
          className="text-foreground font-medium underline underline-offset-2"
        >
          Continue here
        </button>
      </div>

      <div className="mt-auto mb-16">
        <p className="text-sl-600 max-w-75.25 text-center text-xs leading-normal tracking-wide">
          By continuing, you agree to our{" "}
          <a href="https://www.gomash.ai/terms" className="font-bold">
            Terms of Service
          </a>
          &{" "}
          <a href="https://www.gomash.ai/privacy" className="font-bold">
            Privacy Policy
          </a>
        </p>
      </div>

      <SignInSheet
        isOpen={signInOpen}
        onOpenChange={setSignInOpen}
        onSwitchToSignup={() => {
          setSignInOpen(false);
          navigate("/signup");
        }}
      />
    </div>
  );
}
